import React, { useEffect, useState } from 'react';
import { backofficeAuth, backofficeDb } from '../backofficeFirebaseClient';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { normalizeEmail, getAuthErrorMessage } from '../utils/emailUtils';

const Login = ({ onAuthed }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(backofficeAuth, async (user) => {
      if (!user) return;
      // Do not redirect here; just notify parent once
      onAuthed?.({ uid: user.uid, email: user.email });
    });
    return () => unsub();
  }, [onAuthed]);

  const login = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Normalize email to lowercase for consistent authentication
      const normalizedEmail = normalizeEmail(email);
      const cred = await signInWithEmailAndPassword(backofficeAuth, normalizedEmail, password);
      const adminRef = doc(backofficeDb, 'admins', cred.user.uid);
      const adminSnap = await getDoc(adminRef);
      if (!adminSnap.exists()) {
        setError(getAuthErrorMessage('permission-denied'));
      }
    } catch (err) {
      const errorCode = err?.code || err?.message || 'login-failed';
      setError(getAuthErrorMessage(errorCode));
    } finally {
      setLoading(false);
    }
  };

  const bootstrapCreate = async () => {
    setError('');
    setLoading(true);
    try {
      // Normalize email to lowercase for consistent authentication
      const normalizedEmail = normalizeEmail(email);
      const cred = await createUserWithEmailAndPassword(backofficeAuth, normalizedEmail, password);
      const adminRef = doc(backofficeDb, 'admins', cred.user.uid);
      await setDoc(adminRef, { email: cred.user.email, role: 'super_admin', createdAt: serverTimestamp() }, { merge: true });
      onAuthed?.({ uid: cred.user.uid, email: cred.user.email, role: 'super_admin' });
    } catch (err) {
      const errorCode = err?.code || err?.message || 'bootstrap-failed';
      setError(getAuthErrorMessage(errorCode));
    } finally {
      setLoading(false);
    }
  };

  // Only show superadmin creation in development - hide on production domains
  const isProduction = window.location.hostname === 'backoffice.acctoo.com' || 
                       window.location.hostname === 'backoffice-acctoo.web.app';
  const isDevelopment = !isProduction;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-sm bg-white rounded-xl border p-6">
        <div className="text-center mb-4">
          <div className="text-xl font-semibold">Backoffice Login</div>
          <div className="text-xs text-gray-500">Admins only</div>
        </div>
        <form onSubmit={login} className="space-y-3">
          <input
            type="email"
            className="w-full border rounded px-3 py-2"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            className="w-full border rounded px-3 py-2"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && (
            <div className="text-xs text-rose-600">{String(error)}</div>
          )}
          <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white rounded px-3 py-2">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {isDevelopment && (
          <details className="mt-4 text-xs text-gray-500">
            <summary>Bootstrap first superadmin</summary>
            <div className="mt-2">
              <p>Option A: If user already exists but no admin record, sign in first then click below to create your admin record.</p>
              <button
                className="mt-2 border rounded px-2 py-1"
                onClick={async () => {
                  const u = backofficeAuth.currentUser;
                  if (!u) { setError('sign-in first'); return; }
                  const ref = doc(backofficeDb, 'admins', u.uid);
                  await setDoc(ref, { email: u.email, role: 'super_admin', createdAt: serverTimestamp() }, { merge: true });
                  alert('Admin record created. Reload.');
                }}
              >Create superadmin record</button>

              <div className="mt-3" />
              <p>Option B: Create Auth user + admin record in one step (use above email/password). Requires Email/Password sign-in to be enabled in Firebase Authentication.</p>
              <button
                className="mt-2 border rounded px-2 py-1"
                onClick={bootstrapCreate}
                disabled={loading}
              >Create admin user + record</button>
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export default Login;


